import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const MayorResumen = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [resumenFechaDesde, setResumenFechaDesde] = useState("");
  const [resumenFechaHasta, setResumenFechaHasta] = useState("");
  const [resumenEstatusPago, setResumenEstatusPago] = useState("todos");
  const [resumenOficina, setResumenOficina] = useState("todas");
  const [resumenTipoFecha, setResumenTipoFecha] = useState("planilla");
  const [resumenFilters, setResumenFilters] = useState({ codigo: "", impuesto: "", base: "", subtotal: "", multas: "", descuento: "", total: "" });

  useEffect(() => { fetchResumenImpuestoData(); }, []);

  const fetchResumenImpuestoData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      let url = `${API}/reportes/mayor-impuesto-resumen`;
      const params = new URLSearchParams();
      if (aplicarFiltros || resumenFechaDesde || resumenFechaHasta || resumenEstatusPago !== "todos" || resumenOficina !== "todas") {
        if (resumenFechaDesde) params.append("fecha_desde", resumenFechaDesde);
        if (resumenFechaHasta) params.append("fecha_hasta", resumenFechaHasta);
        if (resumenEstatusPago !== "todos") params.append("estatus_pago", resumenEstatusPago);
        if (resumenOficina !== "todas") params.append("oficina", resumenOficina);
        if (resumenTipoFecha !== "planilla") params.append("tipo_fecha", resumenTipoFecha);
      }
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setReporteData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados");
    } catch {
      toast.error("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosResumen = () => {
    setResumenFechaDesde("");
    setResumenFechaHasta("");
    setResumenEstatusPago("todos");
    setResumenOficina("todas");
    setResumenTipoFecha("planilla");
    setResumenFilters({ codigo: "", impuesto: "", base: "", subtotal: "", multas: "", descuento: "", total: "" });
    fetchResumenImpuestoData(false);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosResumenActivos = resumenFechaDesde || resumenFechaHasta || resumenEstatusPago !== "todos" || resumenOficina !== "todas" || resumenTipoFecha !== "planilla";
  const updateResumenFilter = (key, value) => setResumenFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosResumenColActivos = Object.values(resumenFilters).some(v => v !== "");

  const filteredResumenFilas = reporteData?.filas?.filter(f => {
    const rf = resumenFilters;
    if (rf.codigo && !(f.codigo_presupuestario || "").toLowerCase().includes(rf.codigo.toLowerCase())) return false;
    if (rf.impuesto && !(f.impuesto_tasa || "").toLowerCase().includes(rf.impuesto.toLowerCase())) return false;
    if (rf.base && !String(f.base_imponible || 0).includes(rf.base)) return false;
    if (rf.subtotal && !String(f.subtotal_declarado || 0).includes(rf.subtotal)) return false;
    if (rf.multas && !String(f.multas || 0).includes(rf.multas)) return false;
    if (rf.descuento && !String(f.descuento || 0).includes(rf.descuento)) return false;
    if (rf.total && !String(f.total_declarado || 0).includes(rf.total)) return false;
    return true;
  }) || [];

  const resumenTotalesFiltrados = filteredResumenFilas.reduce((acc, f) => ({
    base_imponible: acc.base_imponible + (f.base_imponible || 0),
    subtotal_declarado: acc.subtotal_declarado + (f.subtotal_declarado || 0),
    multas: acc.multas + (f.multas || 0),
    descuento: acc.descuento + (f.descuento || 0),
    total_declarado: acc.total_declarado + (f.total_declarado || 0),
  }), { base_imponible: 0, subtotal_declarado: 0, multas: 0, descuento: 0, total_declarado: 0 });

  const exportResumenToExcel = () => {
    if (!reporteData) return;
    const wsData = [
      ["MAYOR DE IMPUESTO RESUMEN"],
      [`Generado: ${new Date(reporteData.fecha_generacion).toLocaleString("es-VE")}`],
      [],
      ["CODIGO PRESUPUESTARIO", "IMPUESTO/TASA", "BASE IMPONIBLE", "SUBTOTAL DECLARADO", "(+) MULTAS", "(-) DCTO.", "TOTAL DECLARADO"]
    ];
    filteredResumenFilas.forEach(f => wsData.push([f.codigo_presupuestario, f.impuesto_tasa, f.base_imponible, f.subtotal_declarado, f.multas, f.descuento, f.total_declarado]));
    wsData.push([]);
    wsData.push(["", "TOTALES", resumenTotalesFiltrados.base_imponible, resumenTotalesFiltrados.subtotal_declarado, resumenTotalesFiltrados.multas, resumenTotalesFiltrados.descuento, resumenTotalesFiltrados.total_declarado]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 16 }, { wch: 50 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, "ResumenImpuesto");
    XLSX.writeFile(wb, `Mayor_Impuesto_Resumen_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportResumenToPDF = () => {
    if (!reporteData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.setTextColor(109, 40, 217);
    doc.text("MAYOR DE IMPUESTO RESUMEN", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generado: ${new Date(reporteData.fecha_generacion).toLocaleString("es-VE")}`, 14, 25);
    const body = filteredResumenFilas.map(f => [f.codigo_presupuestario, (f.impuesto_tasa || "").substring(0, 45), fmtBs(f.base_imponible), fmtBs(f.subtotal_declarado), fmtBs(f.multas), fmtBs(f.descuento), fmtBs(f.total_declarado)]);
    body.push(["", "TOTALES", fmtBs(resumenTotalesFiltrados.base_imponible), fmtBs(resumenTotalesFiltrados.subtotal_declarado), fmtBs(resumenTotalesFiltrados.multas), fmtBs(resumenTotalesFiltrados.descuento), fmtBs(resumenTotalesFiltrados.total_declarado)]);
    doc.autoTable({
      startY: 32,
      head: [["Codigo Presup.", "Impuesto/Tasa", "Base Imponible", "Subtotal Decl.", "(+) Multas", "(-) Dcto.", "Total Declarado"]],
      body,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [109, 40, 217], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [237, 233, 254]; } }
    });
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(8); doc.setTextColor(128); doc.text(`SIRET - Mayor de Impuesto Resumen | Pagina ${i} de ${pages}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" }); }
    doc.save(`Mayor_Impuesto_Resumen_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportResumenToCSV = () => {
    if (!reporteData) return;
    const header = "CODIGO PRESUPUESTARIO,IMPUESTO/TASA,BASE IMPONIBLE,SUBTOTAL DECLARADO,(+) MULTAS,(-) DCTO.,TOTAL DECLARADO";
    const rows = filteredResumenFilas.map(f => `"${f.codigo_presupuestario}","${f.impuesto_tasa}",${f.base_imponible},${f.subtotal_declarado},${f.multas},${f.descuento},${f.total_declarado}`);
    rows.push(`"","TOTALES",${resumenTotalesFiltrados.base_imponible},${resumenTotalesFiltrados.subtotal_declarado},${resumenTotalesFiltrados.multas},${resumenTotalesFiltrados.descuento},${resumenTotalesFiltrados.total_declarado}`);
    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Mayor_Impuesto_Resumen_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const exportResumenToJSON = () => {
    if (!reporteData) return;
    const blob = new Blob([JSON.stringify({ filas: filteredResumenFilas, totales: resumenTotalesFiltrados }, null, 2)], { type: "application/json" });
    saveAs(blob, `Mayor_Impuesto_Resumen_${new Date().toISOString().split("T")[0]}.json`);
    toast.success("Exportado a JSON");
  };

  const exportResumenToText = () => {
    if (!reporteData) return;
    let txt = "MAYOR DE IMPUESTO RESUMEN\n" + "=".repeat(70) + "\n\n";
    filteredResumenFilas.forEach(f => { txt += `${f.codigo_presupuestario} | ${f.impuesto_tasa}\n  Base Imp: ${fmtBs(f.base_imponible)} | Subtotal: ${fmtBs(f.subtotal_declarado)} | Multas: ${fmtBs(f.multas)} | Dcto: ${fmtBs(f.descuento)} | Total: ${fmtBs(f.total_declarado)}\n` + "-".repeat(70) + "\n"; });
    txt += `\nTOTALES: Base=${fmtBs(resumenTotalesFiltrados.base_imponible)} | Subtotal=${fmtBs(resumenTotalesFiltrados.subtotal_declarado)} | Multas=${fmtBs(resumenTotalesFiltrados.multas)} | Dcto=${fmtBs(resumenTotalesFiltrados.descuento)} | Total=${fmtBs(resumenTotalesFiltrados.total_declarado)}`;
    const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `Mayor_Impuesto_Resumen_${new Date().toISOString().split("T")[0]}.txt`);
    toast.success("Exportado a Texto");
  };

  const exportResumenToHTML = () => {
    if (!reporteData) return;
    let html = `<html><head><meta charset="utf-8"><title>Mayor de Impuesto Resumen</title><style>body{font-family:Arial}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:13px}th{background:#6D28D9;color:#fff}tr:nth-child(even){background:#f5f3ff}.totales{font-weight:bold;background:#ede9fe!important}</style></head><body>`;
    html += `<h2>Mayor de Impuesto Resumen</h2><table><tr><th>Codigo Presup.</th><th>Impuesto/Tasa</th><th>Base Imponible</th><th>Subtotal Decl.</th><th>(+) Multas</th><th>(-) Dcto.</th><th>Total Declarado</th></tr>`;
    filteredResumenFilas.forEach(f => { html += `<tr><td>${f.codigo_presupuestario}</td><td>${f.impuesto_tasa}</td><td style="text-align:right">${fmtBs(f.base_imponible)}</td><td style="text-align:right">${fmtBs(f.subtotal_declarado)}</td><td style="text-align:right">${fmtBs(f.multas)}</td><td style="text-align:right">${fmtBs(f.descuento)}</td><td style="text-align:right">${fmtBs(f.total_declarado)}</td></tr>`; });
    html += `<tr class="totales"><td></td><td>TOTALES</td><td style="text-align:right">${fmtBs(resumenTotalesFiltrados.base_imponible)}</td><td style="text-align:right">${fmtBs(resumenTotalesFiltrados.subtotal_declarado)}</td><td style="text-align:right">${fmtBs(resumenTotalesFiltrados.multas)}</td><td style="text-align:right">${fmtBs(resumenTotalesFiltrados.descuento)}</td><td style="text-align:right">${fmtBs(resumenTotalesFiltrados.total_declarado)}</td></tr></table></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, `Mayor_Impuesto_Resumen_${new Date().toISOString().split("T")[0]}.html`);
    toast.success("Exportado a HTML");
  };

  return (
    <div className="space-y-6" data-testid="reporte-mayor-resumen">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="resumen-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mayor de Impuesto Resumen
            </h1>
            <p className="text-slate-500">Agrupación por código presupuestario e impuesto/tasa</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchResumenImpuestoData(false)} disabled={loading} data-testid="resumen-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!reporteData} data-testid="resumen-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportResumenToExcel} data-testid="resumen-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportResumenToPDF} data-testid="resumen-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportResumenToCSV} data-testid="resumen-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportResumenToJSON} data-testid="resumen-export-json">
                <FileJson className="w-4 h-4 mr-2" /> JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportResumenToText} data-testid="resumen-export-txt">
                <FileType className="w-4 h-4 mr-2" /> Texto
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportResumenToHTML} data-testid="resumen-export-html">
                <FileText className="w-4 h-4 mr-2" /> HTML
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-violet-200 bg-violet-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Tipo de Fecha</Label>
              <Select value={resumenTipoFecha} onValueChange={setResumenTipoFecha}>
                <SelectTrigger className="bg-white" data-testid="resumen-tipo-fecha"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planilla">Fecha Planilla</SelectItem>
                  <SelectItem value="recaudacion">Fecha Recaudación</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Desde</Label>
              <Input type="date" value={resumenFechaDesde} onChange={(e) => setResumenFechaDesde(e.target.value)} className="bg-white" data-testid="resumen-fecha-desde" />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Hasta</Label>
              <Input type="date" value={resumenFechaHasta} onChange={(e) => setResumenFechaHasta(e.target.value)} className="bg-white" data-testid="resumen-fecha-hasta" />
            </div>
            <div className="w-[160px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Estatus Pago</Label>
              <Select value={resumenEstatusPago} onValueChange={setResumenEstatusPago}>
                <SelectTrigger className="bg-white" data-testid="resumen-estatus-pago">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Pendiente">Pendiente</SelectItem>
                  <SelectItem value="Pagado">Pagado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[160px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Oficina</Label>
              <Select value={resumenOficina} onValueChange={setResumenOficina}>
                <SelectTrigger className="bg-white" data-testid="resumen-oficina">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="Hacienda">Hacienda</SelectItem>
                  <SelectItem value="Catastro">Catastro</SelectItem>
                  <SelectItem value="Publicidad">Publicidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchResumenImpuestoData(true)} disabled={loading} className="bg-violet-600 hover:bg-violet-700" data-testid="resumen-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosResumenActivos && (
                <Button variant="outline" onClick={limpiarFiltrosResumen} disabled={loading} data-testid="resumen-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-400" />
        </div>
      ) : reporteData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Planillas</p>
                <p className="text-2xl font-bold text-slate-900">{reporteData.resumen?.total_planillas || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Grupos</p>
                <p className="text-2xl font-bold text-violet-700">{reporteData.resumen?.total_grupos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">(+) Multas</p>
                <p className="text-lg font-bold text-red-600">{fmtBs(reporteData.resumen?.multas)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">(-) Dcto.</p>
                <p className="text-lg font-bold text-amber-600">{fmtBs(reporteData.resumen?.descuento)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Declarado</p>
                <p className="text-lg font-bold text-green-700">{fmtBs(reporteData.resumen?.total_declarado)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabla resumen */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Resumen por Impuesto/Tasa</CardTitle>
                  <CardDescription>{filteredResumenFilas.length} de {reporteData.filas?.length || 0} grupos</CardDescription>
                </div>
                {hayFiltrosResumenColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setResumenFilters({ codigo: "", impuesto: "", base: "", subtotal: "", multas: "", descuento: "", total: "" })} data-testid="resumen-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="resumen-tabla">
                  <thead>
                    <tr className="border-b-2 border-violet-200 bg-violet-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-violet-800 uppercase">Código Presupuestario</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-violet-800 uppercase">Impuesto/Tasa</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-violet-800 uppercase">Base Imponible</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-violet-800 uppercase">Subtotal Declarado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-violet-800 uppercase">(+) Multas</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-violet-800 uppercase">(-) Dcto.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-violet-800 uppercase">Total Declarado</th>
                    </tr>
                    <tr className="bg-violet-50/60 border-b border-violet-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={resumenFilters.codigo} onChange={(e) => updateResumenFilter("codigo", e.target.value)} data-testid="resumen-filter-codigo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={resumenFilters.impuesto} onChange={(e) => updateResumenFilter("impuesto", e.target.value)} data-testid="resumen-filter-impuesto" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={resumenFilters.base} onChange={(e) => updateResumenFilter("base", e.target.value)} data-testid="resumen-filter-base" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={resumenFilters.subtotal} onChange={(e) => updateResumenFilter("subtotal", e.target.value)} data-testid="resumen-filter-subtotal" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={resumenFilters.multas} onChange={(e) => updateResumenFilter("multas", e.target.value)} data-testid="resumen-filter-multas" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={resumenFilters.descuento} onChange={(e) => updateResumenFilter("descuento", e.target.value)} data-testid="resumen-filter-descuento" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={resumenFilters.total} onChange={(e) => updateResumenFilter("total", e.target.value)} data-testid="resumen-filter-total" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResumenFilas.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-12 text-slate-400">No se encontraron registros</td></tr>
                    ) : (
                      filteredResumenFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-violet-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm font-mono text-slate-700">{f.codigo_presupuestario}</td>
                          <td className="py-2.5 px-3 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">{f.impuesto_tasa}</span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.base_imponible)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.subtotal_declarado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-red-600">{fmtBs(f.multas)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-amber-600">{fmtBs(f.descuento)}</td>
                          <td className="py-2.5 px-3 text-sm text-right font-semibold text-slate-900">{fmtBs(f.total_declarado)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredResumenFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-violet-100 border-t-2 border-violet-300">
                        <td colSpan={2} className="py-3 px-3 text-sm font-bold text-violet-900 uppercase">Totales</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-violet-900">{fmtBs(resumenTotalesFiltrados.base_imponible)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-violet-900">{fmtBs(resumenTotalesFiltrados.subtotal_declarado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-red-700">{fmtBs(resumenTotalesFiltrados.multas)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-amber-700">{fmtBs(resumenTotalesFiltrados.descuento)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-green-700">{fmtBs(resumenTotalesFiltrados.total_declarado)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchResumenImpuestoData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default MayorResumen;
